import type { ComponentProps, CSSProperties, HTMLAttributes, ReactElement, ReactNode } from "react";
import { Children, cloneElement, createContext, isValidElement, useContext, useMemo, useRef } from "react";
import { twMerge } from "tailwind-merge";
import { Icon } from "@/components/Icon";
import { InfiniteScrollIndicator } from "@/components/InfiniteScrollIndicator";
import { Skeleton } from "@/components/Skeleton";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { type LoadingState, useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { focusRing } from "@/utils";

export type ActivityFeedVariant = "timeline" | "stacked";
export type ActivityFeedSize = "md" | "sm";
export type ActivityItemTone = "neutral" | "brand" | "success" | "warning" | "danger" | "info";
export type ActivityItemSurface = "minimal" | "subtle" | "solid";

interface ActivityFeedContextValue {
  variant: ActivityFeedVariant;
  size: ActivityFeedSize;
}

const ActivityFeedContext = createContext<ActivityFeedContextValue>({
  variant: "timeline",
  size: "md",
});

function useActivityFeedContext() {
  return useContext(ActivityFeedContext);
}

export interface ActivityFeedProps<T = unknown> extends HTMLAttributes<HTMLDivElement> {
  /** Visual style for the feed */
  variant?: ActivityFeedVariant;
  /** Density of the feed */
  size?: ActivityFeedSize;

  /** Data-driven mode: array of items to render */
  items?: T[];
  /** Render function for each item (when using items array) */
  renderItem?: (item: T, index: number) => ReactNode;

  /** Children mode: manual ActivityItem components */
  children?: ReactNode;

  /** Loading & error UI gates */
  isLoading?: boolean;
  /** Explicit loading state override (takes precedence over auto-detection) */
  loadingState?: LoadingState;
  error?: string | null;
  /** Custom skeleton for initial loading state - matches your activity item structure */
  renderLoadingSkeleton?: () => ReactNode;
  /** Number of skeleton items to show during initial loading */
  loadingSkeletonCount?: number;

  /** Empty state */
  emptyMessage?: string;

  /** Infinite scroll */
  onLoadMore?: () => void | Promise<void>;
  hasMore?: boolean;
  /** Enable virtualization (default: true for 100+ items) */
  enableVirtualization?: boolean;
  /** Estimated size of each item in pixels */
  estimatedItemSize?: number;
  /** Custom loading indicator for loading-more state */
  loadingIndicator?: ReactNode;

  /** Height/maxHeight for scroll container */
  height?: string | number;
  maxHeight?: string | number;
}

function isActivityItem(element: ReactNode): element is ReactElement<ActivityItemProps> {
  return isValidElement<ActivityItemProps>(element) && element.type === ActivityItem;
}

function isActivityGroup(element: ReactNode): element is ReactElement<ActivityFeedGroupInternalProps> {
  return isValidElement<ActivityFeedGroupInternalProps>(element) && element.type === ActivityFeedGroup;
}

export function ActivityFeed<T = unknown>({
  variant = "timeline",
  size = "md",
  className,
  children,
  items,
  renderItem,
  isLoading = false,
  loadingState,
  error = null,
  renderLoadingSkeleton,
  loadingSkeletonCount = 5,
  emptyMessage = "No activity found.",
  onLoadMore,
  hasMore = false,
  enableVirtualization,
  estimatedItemSize,
  loadingIndicator,
  height,
  maxHeight,
  ...rest
}: ActivityFeedProps<T>) {
  const feedRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useBreakpoint();

  // Always use stacked variant on mobile for better space utilization
  const effectiveVariant = isMobile ? "stacked" : variant;

  // Determine mode: items-driven or children-driven
  const isItemsMode = items !== undefined && renderItem !== undefined;

  // Always call the hook to satisfy Rules of Hooks; it handles missing onLoadMore gracefully
  const {
    scrollRef: hookScrollRef,
    loadMoreRef,
    virtualizer,
    virtualItems,
    isLoadingMore,
    computedLoadingState,
  } = useInfiniteScroll({
    items: items || [],
    onLoadMore: isItemsMode ? onLoadMore : undefined,
    hasMore: isItemsMode ? hasMore : false,
    isLoading,
    loadingState,
    enableVirtualization: isItemsMode ? enableVirtualization : false,
    estimatedItemSize: estimatedItemSize || (effectiveVariant === "stacked" ? 120 : 150),
    overscan: 5,
  });

  // Use hook's scroll ref if available in items mode, otherwise use feedRef
  const scrollRef = isItemsMode && hookScrollRef ? hookScrollRef : feedRef;

  const providerValue = useMemo(() => ({ variant: effectiveVariant, size }), [effectiveVariant, size]);

  const gapClass = effectiveVariant === "timeline" ? (size === "sm" ? "gap-4" : "gap-6") : "gap-0";
  const variantClasses =
    effectiveVariant === "stacked"
      ? "divide-y divide-border-subtle rounded-lg border border-border-subtle bg-background-surface"
      : "";

  // Container styles for height control (items mode with infinite scroll)
  const containerStyle: CSSProperties = {};
  if (isItemsMode && onLoadMore) {
    if (height) {
      containerStyle.height = typeof height === "number" ? `${height}px` : height;
      containerStyle.overflowY = "auto";
    } else if (maxHeight) {
      containerStyle.maxHeight = typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight;
      containerStyle.overflowY = "auto";
    } else {
      // Default height for infinite scroll
      containerStyle.height = "600px";
      containerStyle.overflowY = "auto";
    }
  }

  // Mode 2: Items-driven with infinite scroll
  if (isItemsMode) {
    const isInitialLoad = computedLoadingState === "loading" || (isLoading && (!items || items.length === 0));

    // Initial loading state
    if (isInitialLoad) {
      const skeletonContent = renderLoadingSkeleton ? (
        <>
          {Array.from({ length: loadingSkeletonCount }).map((_, index) => (
            <div key={`skeleton-${index}`}>{renderLoadingSkeleton()}</div>
          ))}
        </>
      ) : (
        // Default skeleton - matches ActivityItem structure exactly
        <>
          {Array.from({ length: loadingSkeletonCount }).map((_, index) => {
            // Match ActivityItem's dynamic sizing
            const contentGap = size === "sm" ? "gap-2" : "gap-3";

            if (effectiveVariant === "timeline") {
              // Timeline variant skeleton
              const connectorTopPadding = size === "sm" ? "pt-3" : "pt-4";
              const timelinePadding = size === "sm" ? "p-3" : "p-4";

              return (
                <div key={`skeleton-${index}`} className="group relative grid items-start gap-4 grid-cols-[3rem_1fr]">
                  {/* Connector column with indicator */}
                  <div className={`relative flex h-full flex-col items-center ${connectorTopPadding}`}>
                    <Skeleton width={32} height={32} variant="circle" />
                    {/* Connector line to next item */}
                    {index < loadingSkeletonCount - 1 && (
                      <span className="absolute top-[2rem] left-1/2 w-0 border-l-2 border-dotted -translate-x-1/2 h-[calc(100%+2.5rem)] border-border-muted" />
                    )}
                  </div>

                  {/* Content column with surface styling */}
                  <div
                    className={`min-w-0 flex flex-col ${contentGap} ${timelinePadding} rounded-md border border-border-subtle bg-background-surface`}
                  >
                    {/* Title and timestamp row */}
                    <div className="flex items-center justify-between gap-3">
                      <Skeleton width="50%" height={16} />
                      <Skeleton width="15%" height={12} />
                    </div>
                    {/* Description */}
                    <Skeleton width="85%" height={size === "sm" ? 14 : 15} />
                    <Skeleton width="70%" height={size === "sm" ? 14 : 15} />
                  </div>
                </div>
              );
            } else {
              // Stacked variant skeleton
              const stackedPadding = size === "sm" ? "px-3 py-3" : "px-4 py-4";

              return (
                <div key={`skeleton-${index}`} className={`flex flex-col ${stackedPadding}`}>
                  {/* Indicator and title row */}
                  <div className="flex items-center gap-3">
                    <Skeleton width={24} height={24} variant="circle" />
                    <Skeleton width="50%" height={16} />
                  </div>
                  {/* Content section */}
                  <div className={`mt-3 flex flex-col ${contentGap}`}>
                    <Skeleton width="85%" height={size === "sm" ? 14 : 15} />
                    <Skeleton width="70%" height={size === "sm" ? 14 : 15} />
                    <Skeleton width="20%" height={12} />
                  </div>
                </div>
              );
            }
          })}
        </>
      );

      return (
        <ActivityFeedContext.Provider value={providerValue}>
          <div
            ref={scrollRef}
            data-slot="feed"
            data-variant={effectiveVariant}
            className={twMerge("flex flex-col", gapClass, variantClasses, className)}
            style={containerStyle}
            {...rest}
          >
            {skeletonContent}
          </div>
        </ActivityFeedContext.Provider>
      );
    }

    // Error state
    if (error) {
      return (
        <ActivityFeedContext.Provider value={providerValue}>
          <div
            ref={scrollRef}
            data-slot="feed"
            data-variant={effectiveVariant}
            className={twMerge("flex flex-col", gapClass, variantClasses, className)}
            style={containerStyle}
            {...rest}
          >
            <div className="p-4 text-center text-feedback-error-text">{error}</div>
          </div>
        </ActivityFeedContext.Provider>
      );
    }

    // Empty state
    if (!items || items.length === 0) {
      return (
        <ActivityFeedContext.Provider value={providerValue}>
          <div
            ref={scrollRef}
            data-slot="feed"
            data-variant={effectiveVariant}
            className={twMerge("flex flex-col", gapClass, variantClasses, className)}
            style={containerStyle}
            {...rest}
          >
            <div className="p-4 text-center text-text-secondary">{emptyMessage}</div>
          </div>
        </ActivityFeedContext.Provider>
      );
    }

    // Determine which items to render (virtualized or all)
    const itemsToRender = virtualizer
      ? virtualItems?.map((virtualItem) => ({
          virtualItem,
          item: items[virtualItem.index],
          index: virtualItem.index,
        })) || []
      : items.map((item, index) => ({ item, index, virtualItem: null }));

    // Render items
    return (
      <ActivityFeedContext.Provider value={providerValue}>
        <div
          ref={scrollRef}
          data-slot="feed"
          data-variant={effectiveVariant}
          className={twMerge("flex flex-col", gapClass, variantClasses, className)}
          style={{
            ...containerStyle,
            position: virtualizer ? "relative" : undefined,
          }}
          {...rest}
        >
          {virtualizer ? (
            // Virtualized rendering
            <div
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                position: "relative",
              }}
            >
              {itemsToRender.map(({ item, index, virtualItem }) => {
                if (!item || !virtualItem) return null;
                const renderedItem = renderItem(item, index);
                // For timeline variant, inject showConnector prop to last item
                const itemContent =
                  effectiveVariant === "timeline" && isValidElement(renderedItem) && isActivityItem(renderedItem)
                    ? cloneElement(renderedItem, {
                        showConnector: index !== items.length - 1,
                      })
                    : renderedItem;

                return (
                  <div
                    key={`item-${index}`}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                    data-index={virtualItem.index}
                    ref={virtualizer.measureElement}
                  >
                    {itemContent}
                  </div>
                );
              })}

              {/* Loading more indicator for virtualized */}
              {isLoadingMore && (
                <div
                  style={{
                    position: "absolute",
                    top: `${virtualizer.getTotalSize()}px`,
                    left: 0,
                    width: "100%",
                  }}
                >
                  {loadingIndicator || <InfiniteScrollIndicator state="loading-more" />}
                </div>
              )}
            </div>
          ) : (
            // Non-virtualized rendering
            <>
              {itemsToRender.map(({ item, index }) => {
                const renderedItem = renderItem(item, index);
                // For timeline variant, inject showConnector prop to last item
                if (effectiveVariant === "timeline" && isValidElement(renderedItem) && isActivityItem(renderedItem)) {
                  const isLastItem = index === items.length - 1;
                  return (
                    <div key={`item-${index}`}>
                      {cloneElement(renderedItem, {
                        showConnector: !isLastItem,
                      })}
                    </div>
                  );
                }
                return <div key={`item-${index}`}>{renderedItem}</div>;
              })}

              {/* Loading more indicator */}
              {isLoadingMore && (loadingIndicator || <InfiniteScrollIndicator state="loading-more" />)}
            </>
          )}

          {/* Intersection observer target */}
          {onLoadMore && hasMore && !isInitialLoad && (
            <div
              ref={loadMoreRef}
              style={
                virtualizer
                  ? {
                      position: "absolute",
                      top: `${virtualizer.getTotalSize()}px`,
                      left: 0,
                      width: "100%",
                      height: 1,
                    }
                  : { height: 1, width: "100%" }
              }
            />
          )}
        </div>
      </ActivityFeedContext.Provider>
    );
  }

  // Mode 1: Children-driven (existing behavior)
  const childArray = Children.toArray(children);
  const processedChildren = childArray.map((child, index) => {
    if (isActivityItem(child)) {
      const showConnector =
        child.props.showConnector ?? (effectiveVariant === "timeline" && index < childArray.length - 1);
      const showIncomingConnector = effectiveVariant === "timeline" && index > 0;
      return cloneElement(child, { showConnector, showIncomingConnector });
    }

    if (isActivityGroup(child)) {
      const isLastGroup = index === childArray.length - 1;
      const isFirstGroup = index === 0;
      return cloneElement(child, {
        __isLastGroup: isLastGroup,
        __isFirstGroup: isFirstGroup,
      });
    }

    return child;
  });

  return (
    <ActivityFeedContext.Provider value={providerValue}>
      <div
        data-slot="feed"
        data-variant={effectiveVariant}
        className={twMerge("flex flex-col", gapClass, variantClasses, className)}
        {...rest}
      >
        {processedChildren}
      </div>
    </ActivityFeedContext.Provider>
  );
}

ActivityFeed.displayName = "ActivityFeed";

export interface ActivityFeedGroupProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  /** Heading shown above the grouped items */
  title?: ReactNode;
  /** Optional description that provides additional context */
  description?: ReactNode;
  /** Optional trailing actions for the group */
  actions?: ReactNode;
  /** Group content */
  children: ReactNode;
}

interface ActivityFeedGroupInternalProps extends ActivityFeedGroupProps {
  __isLastGroup?: boolean;
  __isFirstGroup?: boolean;
}

export function ActivityFeedGroup(props: ActivityFeedGroupInternalProps) {
  const { variant, size } = useActivityFeedContext();
  const {
    title,
    description,
    actions,
    children,
    className,
    __isLastGroup = false,
    __isFirstGroup = false,
    ...rest
  } = props;

  const childArray = Children.toArray(children);
  const processedChildren = childArray.map((child, index) => {
    if (isActivityItem(child)) {
      const showConnector =
        child.props.showConnector ?? (variant === "timeline" && !(__isLastGroup && index === childArray.length - 1));
      const showIncomingConnector = variant === "timeline" && !(__isFirstGroup && index === 0);
      return cloneElement(child, { showConnector, showIncomingConnector });
    }
    return child;
  });

  const headerPadding = variant === "timeline" ? "pl-[3rem]" : size === "sm" ? "px-3" : "px-4";
  const contentGap = size === "sm" ? "gap-3" : "gap-4";

  return (
    <section data-slot="group" className={twMerge("flex flex-col", className)} {...rest}>
      {(title || description || actions) && (
        <div className={twMerge("mb-2 flex flex-wrap items-end justify-between gap-2", headerPadding)}>
          <div className="flex min-w-0 flex-col gap-1">
            {title && (
              <span className="text-xs font-semibold uppercase tracking-wide text-text-secondary">{title}</span>
            )}
            {description && <span className="text-sm text-text-secondary">{description}</span>}
          </div>
          {actions && <div className="flex flex-shrink-0 items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className={twMerge("flex flex-col", contentGap)}>{processedChildren}</div>
    </section>
  );
}

ActivityFeedGroup.displayName = "ActivityFeedGroup";

export interface ActivityItemProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  /** Primary label describing the activity */
  title: ReactNode;
  /** Supplementary copy beneath the title */
  description?: ReactNode;
  /** Timestamp or contextual information shown opposite the title */
  timestamp?: ReactNode;
  /** Additional metadata shown below the description */
  meta?: ReactNode;
  /** Supplementary media such as attachments, badges, or chips */
  media?: ReactNode;
  /** Optional action buttons or controls */
  actions?: ReactNode;
  /** Visual element displayed inside the indicator */
  visual?: ReactNode;
  /** Shortcut for displaying an icon in the indicator */
  icon?: ComponentProps<typeof Icon>["name"];
  /** Icon weight for the indicator icon */
  iconWeight?: ComponentProps<typeof Icon>["weight"];
  /** Visual tone applied to the indicator */
  tone?: ActivityItemTone;
  /** Surface treatment for the content */
  surface?: ActivityItemSurface;
  /** Highlights the item using the tone color */
  isHighlighted?: boolean;
  /** Whether to render a connector line for timeline layouts */
  showConnector?: boolean;
  /** Whether to render the incoming connector line from previous item (internal) */
  showIncomingConnector?: boolean;
  /** Hides the indicator entirely */
  hideIndicator?: boolean;
  /** Marks the item as interactive, enabling keyboard semantics */
  interactive?: boolean;
}

const indicatorToneStyles: Record<ActivityItemTone, string> = {
  neutral: "bg-background-muted text-text-secondary border-border-muted",
  brand: "bg-background-muted text-action-brand border-action-brand/30",
  success: "bg-background-muted text-feedback-success-text border-feedback-success-border/40",
  warning: "bg-background-muted text-feedback-warning-text border-feedback-warning-border/40",
  danger: "bg-background-muted text-feedback-error-text border-feedback-error-border/40",
  info: "bg-background-muted text-feedback-info-text border-feedback-info-border/40",
};

const highlightBackgroundStyles: Record<ActivityItemTone, string> = {
  neutral: "bg-background-subtle",
  brand: "bg-action-brand/5",
  success: "bg-feedback-success-background",
  warning: "bg-feedback-warning-background",
  danger: "bg-feedback-error-background",
  info: "bg-feedback-info-background",
};

const highlightBorderStyles: Record<ActivityItemTone, string> = {
  neutral: "border-border-muted",
  brand: "border-action-brand",
  success: "border-feedback-success-border",
  warning: "border-feedback-warning-border",
  danger: "border-feedback-error-border",
  info: "border-feedback-info-border",
};

export function ActivityItem(props: ActivityItemProps) {
  const { variant, size } = useActivityFeedContext();
  const {
    title,
    description,
    timestamp,
    meta,
    media,
    actions,
    visual,
    icon,
    iconWeight,
    tone = "neutral",
    surface,
    isHighlighted = false,
    showConnector = true,
    hideIndicator = false,
    interactive,
    className,
    children,
    onKeyDown,
    showIncomingConnector: _showIncomingConnector, // consume but don't use (managed automatically)
    ...rest
  } = props;

  const indicatorContent =
    visual ?? (icon ? <Icon name={icon} weight={iconWeight || "fill"} className="h-4 w-4" /> : null);

  const effectiveSurface = surface ?? (variant === "stacked" ? "minimal" : "subtle");
  const contentGap = size === "sm" ? "gap-2" : "gap-3";
  const timestampClass = "text-xs text-text-secondary";
  const descriptionClass = size === "sm" ? "text-sm" : "text-[15px]";
  const metaClass = "text-xs text-text-secondary";

  const isInteractive = interactive ?? typeof rest.onClick === "function";

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (!isInteractive) {
      onKeyDown?.(event);
      return;
    }
    if (event.key === " ") {
      // Prevent page scroll; activation occurs on keyup.
      event.preventDefault();
    } else if (event.key === "Enter") {
      event.preventDefault();
      (event.currentTarget as HTMLElement).click();
    }
    onKeyDown?.(event);
  };

  const handleKeyUp: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (!isInteractive) return;
    if (event.key === " ") {
      event.preventDefault();
      (event.currentTarget as HTMLElement).click();
    }
  };

  const baseInteractiveClasses = isInteractive ? twMerge("group cursor-pointer", focusRing()) : undefined;

  const connectorTopPadding =
    effectiveSurface === "minimal" ? (size === "sm" ? "pt-2" : "pt-3") : size === "sm" ? "pt-3" : "pt-4";

  const connector = (
    <div data-slot="connector" className={twMerge("relative flex h-full flex-col items-center", connectorTopPadding)}>
      <span
        data-slot="indicator"
        className={twMerge(
          "relative z-10 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border text-sm font-medium shadow-sm ring-2 ring-background-surface",
          indicatorToneStyles[tone]
        )}
      >
        {indicatorContent}
      </span>
      {/* Line connecting TO next item - only render if there's a next item */}
      {variant === "timeline" && showConnector && (
        <span
          data-slot="separator"
          className="absolute top-[2rem] left-1/2 w-0 border-l-2 border-dotted -translate-x-1/2 h-[calc(100%+2.5rem)] border-border-muted"
        />
      )}
    </div>
  );

  const highlightClasses = isHighlighted
    ? twMerge(
        highlightBackgroundStyles[tone],
        effectiveSurface === "minimal" ? "rounded-md border border-border-muted" : highlightBorderStyles[tone]
      )
    : "";

  if (variant === "stacked") {
    const stackedPadding = size === "sm" ? "px-3 py-3" : "px-4 py-4";
    const stackedSurfaceClasses: Record<ActivityItemSurface, string> = {
      minimal: "",
      subtle: "bg-background-subtle",
      solid: "bg-background-muted",
    };

    return (
      <div
        data-slot="item"
        data-variant="stacked"
        role={isInteractive ? "button" : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        className={twMerge(
          "flex flex-col",
          stackedPadding,
          stackedSurfaceClasses[effectiveSurface],
          highlightClasses,
          baseInteractiveClasses,
          isInteractive ? "transition-colors hover:bg-background-subtle/80" : "",
          className
        )}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        {...rest}
      >
        <div className="flex items-center gap-3">
          {!hideIndicator && (
            <span
              data-slot="indicator"
              className={twMerge(
                "flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border text-sm font-medium shadow-sm ring-2 ring-background-surface",
                indicatorToneStyles[tone]
              )}
            >
              {indicatorContent}
            </span>
          )}
          <div data-slot="title" className="min-w-0 flex-1 text-sm font-semibold text-text-primary">
            {title}
          </div>
        </div>
        <div data-slot="content" className={twMerge("mt-3 flex flex-col", contentGap)}>
          {description && (
            <div data-slot="description" className={twMerge("text-text-secondary", descriptionClass)}>
              {description}
            </div>
          )}
          {meta && <div className={metaClass}>{meta}</div>}
          {media && <div className="flex flex-col gap-2 text-sm text-text-secondary">{media}</div>}
          {children}
          {actions && <div className="flex flex-wrap gap-2 pt-1">{actions}</div>}
          {timestamp && <div className={twMerge("-mt-1.5", timestampClass)}>{timestamp}</div>}
        </div>
      </div>
    );
  }

  const timelinePadding =
    effectiveSurface === "minimal" ? (size === "sm" ? "py-2" : "py-3") : size === "sm" ? "p-3" : "p-4";
  const surfaceClasses: Record<ActivityItemSurface, string> = {
    minimal: "", // No border, no background
    subtle: "rounded-md border border-border-subtle bg-background-surface", // Border + background
    solid: "rounded-md bg-background-muted shadow-sm", // Background only, no border
  };

  return (
    <div
      data-slot="item"
      data-variant="timeline"
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      className={twMerge(
        "group relative grid items-start gap-4 grid-cols-[3rem_1fr]",
        baseInteractiveClasses,
        className
      )}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      {...rest}
    >
      {hideIndicator ? <div /> : connector}
      <div
        data-slot="content"
        className={twMerge(
          "min-w-0 flex flex-col",
          contentGap,
          timelinePadding,
          surfaceClasses[effectiveSurface],
          highlightClasses,
          isInteractive ? "transition-colors group-hover:bg-background-subtle/70" : ""
        )}
      >
        <div className="flex items-center justify-between gap-3">
          <div data-slot="title" className="min-w-0 flex-1 text-sm font-semibold text-text-primary">
            {title}
          </div>
          {timestamp && <div className={twMerge("flex-shrink-0", timestampClass)}>{timestamp}</div>}
        </div>
        {description && (
          <div data-slot="description" className={twMerge("text-text-secondary", descriptionClass)}>
            {description}
          </div>
        )}
        {meta && <div className={metaClass}>{meta}</div>}
        {media && <div className="flex flex-col gap-2 text-sm text-text-secondary">{media}</div>}
        {children}
        {actions && <div className="flex flex-wrap gap-2 pt-2">{actions}</div>}
      </div>
    </div>
  );
}

ActivityItem.displayName = "ActivityItem";
